import type { FieldErrors } from '@/types';
import * as v from 'valibot';
import Converter from '@/utils/converter';
import Phone from '@/utils/phone';

export type Salutation = 'Herr' | 'Frau' | 'Divers' | 'Eheleute' | 'unknown';

interface NameCodes {
  required: string;
  tooShort: string;
  tooLong: string;
}

interface AgreementOptions {
  asNumberString?: boolean;
}

const SUSPICIOUS_PREFIX = 'suspicious:';

const suspicious = (code: string): string => `${SUSPICIOUS_PREFIX}${code}`;

export const FormSchemaCode = {
  SalutationRequired: 'salutation:required',
  FirstNameRequired: 'first_name:required',
  FirstNameTooShort: 'first_name:too-short',
  FirstNameTooLong: 'first_name:too-long',
  LastNameRequired: 'last_name:required',
  LastNameTooShort: 'last_name:too-short',
  LastNameTooLong: 'last_name:too-long',
  EmailRequired: 'email:required',
  EmailInvalid: 'email:invalid',
  PhoneRequired: 'phone:required',
  PhoneTooShort: 'phone:too-short',
  PhoneTooLong: 'phone:too-long',
  PhoneSuspiciousTooShort: suspicious('phone:too-short'),
  PhoneSuspiciousTooLong: suspicious('phone:too-long'),
  PhoneInvalidPattern: suspicious('phone:invalid-pattern'),
  PhoneUnknownCountry: suspicious('phone:unknown-country'),
  PhoneUnrecognised: suspicious('phone:unrecognised'),
  PhoneDoubledCountryCode: suspicious('phone:doubled-country-code'),
} as const;

const NAME_MIN_LENGTH = 2;
const NAME_MAX_LENGTH = 32;

export default class FormSchema {
  private static hidden() {
    return v.literal('');
  }

  private static get defaultErrors(): FieldErrors {
    return {
      [FormSchemaCode.PhoneRequired]: 'Bitte geben Sie Ihre Telefonnummer ein.',
      [FormSchemaCode.PhoneTooShort]: 'Die Telefonnummer ist zu kurz.',
      [FormSchemaCode.PhoneTooLong]: 'Die Telefonnummer ist zu lang.',
      [FormSchemaCode.PhoneSuspiciousTooShort]:
        'Die Telefonnummer scheint zu kurz zu sein. Bitte prüfen Sie die Nummer.',
      [FormSchemaCode.PhoneSuspiciousTooLong]:
        'Die Telefonnummer scheint zu lang zu sein. Bitte prüfen Sie die Nummer.',
      [FormSchemaCode.PhoneInvalidPattern]: 'Bitte geben Sie eine gültige Telefonnummer ein.',
      [FormSchemaCode.PhoneUnknownCountry]:
        'Die Landesvorwahl konnten wir keinem Land zuordnen. Bitte prüfen Sie die Nummer.',
      [FormSchemaCode.PhoneUnrecognised]: 'Diese Telefonnummer konnten wir nicht lesen. Bitte prüfen Sie die Nummer.',
      [FormSchemaCode.PhoneDoubledCountryCode]:
        'Die Landesvorwahl 49 steht doppelt. Bitte prüfen Sie die vorgeschlagene Nummer.',
    };
  }

  public static salutation(keys: string[], choices: Salutation[]) {
    if (!keys.includes('salutation')) return FormSchema.hidden();
    return v.union(
      choices.map((choice) => v.literal(choice)),
      FormSchemaCode.SalutationRequired,
    );
  }

  private static name(codes: NameCodes) {
    return v.pipe(
      v.string(),
      v.nonEmpty(codes.required),
      v.minLength(NAME_MIN_LENGTH, codes.tooShort),
      v.maxLength(NAME_MAX_LENGTH, codes.tooLong),
    );
  }

  public static firstName(keys: string[]) {
    if (!keys.includes('first_name')) return FormSchema.hidden();
    return FormSchema.name({
      required: FormSchemaCode.FirstNameRequired,
      tooShort: FormSchemaCode.FirstNameTooShort,
      tooLong: FormSchemaCode.FirstNameTooLong,
    });
  }

  public static lastName(keys: string[]) {
    if (!keys.includes('last_name')) return FormSchema.hidden();
    return FormSchema.name({
      required: FormSchemaCode.LastNameRequired,
      tooShort: FormSchemaCode.LastNameTooShort,
      tooLong: FormSchemaCode.LastNameTooLong,
    });
  }

  public static email(keys: string[]) {
    if (!keys.includes('email')) return FormSchema.hidden();
    return v.pipe(v.string(), v.nonEmpty(FormSchemaCode.EmailRequired), v.email(FormSchemaCode.EmailInvalid));
  }

  public static phone(keys: string[]) {
    if (!keys.includes('phone')) return FormSchema.hidden();
    return v.pipe(
      v.string(),
      v.trim(),
      v.nonEmpty(FormSchemaCode.PhoneRequired),
      v.check((value) => Phone.error(value) !== 'too-short', FormSchemaCode.PhoneTooShort),
      v.check((value) => Phone.error(value) !== 'too-long', FormSchemaCode.PhoneTooLong),
      v.check((value) => Phone.suspicion(value) !== 'too-short', FormSchemaCode.PhoneSuspiciousTooShort),
      v.check((value) => Phone.suspicion(value) !== 'too-long', FormSchemaCode.PhoneSuspiciousTooLong),
      v.check((value) => Phone.suspicion(value) !== 'invalid-pattern', FormSchemaCode.PhoneInvalidPattern),
      v.check((value) => Phone.suspicion(value) !== 'unknown-country', FormSchemaCode.PhoneUnknownCountry),
      v.check((value) => Phone.suspicion(value) !== 'unrecognised', FormSchemaCode.PhoneUnrecognised),
      v.check((value) => Phone.suspicion(value) !== 'doubled-country-code', FormSchemaCode.PhoneDoubledCountryCode),
    );
  }

  public static agreement(keys: string[], key: string, options: AgreementOptions = {}) {
    if (!keys.includes(key)) return FormSchema.hidden();
    const required = v.pipe(v.string(), v.literal('true', `${key}:required`));
    if (!options.asNumberString) return required;
    return v.pipe(
      required,
      v.transform((value) => Converter.BooleanString.ToBooleanNumberString(value)),
    );
  }

  public static suspicious(code: string) {
    return suspicious(code);
  }

  public static isSuspicious(code: string) {
    return code.startsWith(SUSPICIOUS_PREFIX);
  }

  public static message(code: string | undefined, overrides: FieldErrors = {}) {
    if (!code) return null;
    return { ...FormSchema.defaultErrors, ...overrides }[code] ?? null;
  }
}
